/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  FlexContainer,
  Input,
  Sidebar,
  Switch,
  theme,
} from "@nordcloud/gnui";
import { useGetAppsWithEnvironmentsQuery } from "~/generated/graphql";
import { UniversalWrap, GlobalSpinner } from "~/components";
import { ErrorMessageWithRecover } from "~/components/Error";
import { ListLoader } from "~/components/Loaders/ListLoader";
import { ERROR_TEXT } from "~/constants";
import { showError } from "~/services/toast";
import { isEmpty, isNotEmpty, Maybe, removeFromApolloCache } from "~/tools";
import { AttachEnvsTable } from "./AttachEnvsTable";
import { ConflictsList } from "./ConflictsList";
import { useAttachEnvs } from "./hooks/useAttachEnvs";
import { Environment, filterApps } from "./utils";

type Application = {
  name: string;
  id: string;
  nid: string;
  environments?: Maybe<Maybe<Environment>[]>;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onChange: () => void;
};

type RouteParams = {
  structureId: string;
  unitId: string;
};

export function AttachEnvs({ isOpen, onClose, onChange }: Props) {
  const {
    data,
    loading: isLoadingAppsWithEnvironments,
    error,
    refetch,
    client,
  } = useGetAppsWithEnvironmentsQuery({
    notifyOnNetworkStatusChange: true,
    skip: !isOpen,
  });

  const [isLoading, setIsLoading] = useState(false);
  const { structureId = "", unitId: orgUnitId = "" } = useParams<RouteParams>();

  // Avoid mapping and assigning default values for nullable fields due to performance reasons
  const applications = (data?.applications ?? []) as Application[];

  const {
    envs,
    setEnvs,
    filter,
    setFilter,
    envsToRemove,
    setEnvsToRemove,
    isSelectedAllEnvironments,
    setIsSelectedAllEnvironments,
    reset,
  } = useEnvironmentSelector();

  const {
    attachSelectedEnvs,
    unattachSelectedEnvs,
    attachmentConflicts,
    setAttachmentConflicts,
  } = useAttachEnvs({
    selectedAllEnvironments: isSelectedAllEnvironments,
    applications,
    filter,
    envs,
    envsToRemove,
  });

  const onSave = async () => {
    if (isEmpty(envs) && isEmpty(envsToRemove) && !isSelectedAllEnvironments) {
      showError(ERROR_TEXT.failedSettingCostSplittingEnv, {
        autoClose: false,
      });

      return;
    }

    setIsLoading(true);

    await attachSelectedEnvs();

    if (!isSelectedAllEnvironments) {
      await unattachSelectedEnvs();
    }

    setIsLoading(false);

    onChange();
    onClose();

    // Clear cache of `useGetAppsWithEnvironmentsQuery` to force network request next time `AttachEnvs` is opened
    removeFromApolloCache(client.cache, { fieldName: "applications" });
  };

  const onClear = () => {
    reset();
    setEnvsToRemove(applications.flatMap((app) => app.environments));
  };

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
    // Prevent unintentionally resetting hook state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const closeConflicts = () => {
    setAttachmentConflicts([]);
  };

  const handleConflictsAttachment = () => {
    closeConflicts();
    onChange();
  };

  return (
    <>
      <GlobalSpinner loading={isLoading} />
      <Sidebar
        isOpen={isOpen}
        title="Select application environments"
        footer={
          <FlexContainer
            p={theme.spacing.spacing04}
            gap={theme.spacing.spacing02}
          >
            <Button onClick={onSave}>Save</Button>
            <Button severity="medium" onClick={onClear}>
              Clear
            </Button>
          </FlexContainer>
        }
        onClick={onClose}
      >
        <UniversalWrap
          loaderProps={{
            loading: isLoadingAppsWithEnvironments,
            Component: ListLoader,
          }}
          errorProps={{
            error,
            Component: ErrorMessageWithRecover,
            onClick: () => refetch(),
          }}
        >
          <Input
            small
            type="filter"
            value={filter}
            name="filter"
            placeholder="Filter"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setFilter(event.target.value)
            }
          />
          <FlexContainer
            justifyContent="flex-end"
            margin={theme.spacing.spacing04}
          >
            <Switch
              id="all-envs"
              data-testid="all-envs"
              position="left"
              checked={isSelectedAllEnvironments}
              labelText={`All ${
                isNotEmpty(filter) ? "filtered " : ""
              }environments`}
              onChange={() =>
                setIsSelectedAllEnvironments((prevState) => !prevState)
              }
            />
          </FlexContainer>
          <AttachEnvsTable
            selectedAllEnvironments={isSelectedAllEnvironments}
            filteredApps={filterApps(applications, filter)}
            envs={envs}
            setEnvs={setEnvs}
            envsToRemove={envsToRemove}
            setEnvsToRemove={setEnvsToRemove}
          />
        </UniversalWrap>
      </Sidebar>
      <ConflictsList
        attachmentConflicts={attachmentConflicts}
        structureId={structureId}
        orgUnitId={orgUnitId}
        onAttached={handleConflictsAttachment}
        onClose={closeConflicts}
      />
    </>
  );
}

function useEnvironmentSelector() {
  const [envs, setEnvs] = useState<Maybe<Environment>[]>([]);
  const [envsToRemove, setEnvsToRemove] = useState<Maybe<Environment>[]>([]);
  const [isSelectedAllEnvironments, setIsSelectedAllEnvironments] =
    useState(false);
  const [filter, setFilter] = useState<string>("");

  const reset = () => {
    setFilter("");
    setEnvs([]);
    setEnvsToRemove([]);
    setIsSelectedAllEnvironments(false);
  };

  return {
    envs,
    setEnvs,
    envsToRemove,
    setEnvsToRemove,
    filter,
    setFilter,
    isSelectedAllEnvironments,
    setIsSelectedAllEnvironments,
    reset,
  };
}
